import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { config } from '../config';
import PageHeader from './PageHeader';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Chip
} from '@mui/material';
import { 
  Trash, 
  Plus, 
  Settings,
  Key,
  Globe,
  ShoppingBag,
  User as UserIcon
} from 'lucide-react';
import HelpOutline from '@mui/icons-material/HelpOutline';
import ExpandMore from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import AmazonSettings from './AmazonSettings';
import SecureCredentialsField from './SecureCredentialsField';

// Custom TabPanel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function UserDashboard() {
  const { getAccessTokenSilently, user, isLoading: authLoading } = useAuth0();
  const [activeTab, setActiveTab] = useState(2);
  const [apiKeys, setApiKeys] = useState({
    openai: '',
    anthropic: '',
    fal: '',
    ideogram: '',
    openrouter: '',
    scrapeowl: ''
  });
  const [isLoadingApiKeys, setIsLoadingApiKeys] = useState(true);
  const [isLoadingWordPress, setIsLoadingWordPress] = useState(true);
  const [isLoadingAmazon, setIsLoadingAmazon] = useState(true);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [amazonSettings, setAmazonSettings] = useState({
    api_key: '',
    secret_key: '',
    tracking_id: '',
    marketplace: 'amazon.com'
  });
  const [sites, setSites] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingError, setLoadingError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [siteToDelete, setSiteToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [siteForm, setSiteForm] = useState({
    name: '',
    url: '',
    username: '',
    password: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Help content for API Keys
  const apiHelpContent = (
    <Accordion>
      <AccordionSummary 
        expandIcon={<ExpandMore />}
        sx={{
          backgroundColor: 'rgba(224, 76, 22, 0.05)',
          '&:hover': {
            backgroundColor: 'rgba(224, 76, 22, 0.1)',
          }
        }}
      >
        <Typography variant="h6" sx={{ 
          fontWeight: 600, 
          color: '#E04C16',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Key size={20} /> API Keys Setup Guide
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Security Notice */}
          <Box sx={{ mb: 2 }}>
            <Alert 
              severity="warning" 
              sx={{ 
                borderRadius: 2,
                backgroundColor: '#fff8f6',
                border: '1px solid #fee2d6',
                '& .MuiAlert-icon': { color: '#E04C16' },
                '& .MuiAlert-message': { color: '#9a3412' }
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Important Security Notice
              </Typography>
              <Typography variant="body2">
                As per our terms and conditions, you are fully responsible for keeping your API keys secure. We strongly recommend:
                <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                  <li>Rotating your API keys every 30 days</li>
                  <li>Never sharing your API keys with others</li>
                  <li>Using environment variables in development</li>
                  <li>Monitoring API usage regularly</li>
                </Box>
              </Typography>
            </Alert>
          </Box>

          {/* OpenAI Section */}
          <Box>
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 600, 
              color: '#E04C16',
              mb: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              OpenAI API Key
            </Typography>
            <Box sx={{ 
              backgroundColor: '#f8fafc', 
              border: '1px solid #e2e8f0', 
              borderRadius: 2,
              p: 2
            }}>
              <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                1. Visit <a href="https://platform.openai.com" target="_blank" rel="noopener noreferrer" style={{ color: '#E04C16' }}>platform.openai.com</a><br />
                2. Sign up or log in to your account<br />
                3. Go to "API Keys" in your dashboard<br />
                4. Click "Create new secret key"<br />
                5. Copy the generated key (you won't be able to see it again)<br />
                6. Paste it in the field above and save
              </Typography>
            </Box>
          </Box>

          {/* OpenRouter Section */}
          <Box>
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 600, 
              color: '#E04C16',
              mb: 1.5
            }}>
              OpenRouter API Key
            </Typography>
            <Box sx={{ 
              backgroundColor: '#f8fafc', 
              border: '1px solid #e2e8f0', 
              borderRadius: 2,
              p: 2
            }}>
              <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                1. Go to <a href="https://openrouter.ai" target="_blank" rel="noopener noreferrer" style={{ color: '#E04C16' }}>openrouter.ai</a><br />
                2. Sign up or log in to your account<br />
                3. Navigate to "Keys" in the left sidebar<br />
                4. Click "Generate Key" and name it "Scripti"<br />
                5. Copy the generated key<br />
                6. Paste it in the field above and save
              </Typography>
            </Box>
          </Box>

          {/* Fal.ai Section */}
          <Box>
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 600, 
              color: '#E04C16',
              mb: 1.5
            }}>
              Fal.ai API Key
            </Typography>
            <Box sx={{ 
              backgroundColor: '#f8fafc', 
              border: '1px solid #e2e8f0', 
              borderRadius: 2,
              p: 2
            }}>
              <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                1. Go to <a href="https://fal.ai" target="_blank" rel="noopener noreferrer" style={{ color: '#E04C16' }}>fal.ai</a><br />
                2. Create an account or log in<br />
                3. Navigate to your account settings<br />
                4. Find the API Keys section<br />
                5. Generate a new API key<br />
                6. Copy the key and paste it above
              </Typography>
            </Box>
          </Box>

          {/* Best Practices */}
          <Box>
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 600, 
              color: '#E04C16',
              mb: 1.5
            }}>
              Best Practices
            </Typography>
            <Box sx={{ 
              backgroundColor: '#f8fafc', 
              border: '1px solid #e2e8f0', 
              borderRadius: 2,
              p: 2
            }}>
              <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                • Set up usage limits in your API provider dashboards<br />
                • Keep track of your API costs and usage<br />
                • Save your keys immediately after generating them<br />
                • Delete any unused or old API keys<br />
                • Check API provider documentation for rate limits
              </Typography>
            </Box>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  // Help content for WordPress
  const wordpressHelpContent = (
    <Accordion>
      <AccordionSummary 
        expandIcon={<ExpandMore />}
        sx={{
          backgroundColor: 'rgba(224, 76, 22, 0.05)',
          '&:hover': {
            backgroundColor: 'rgba(224, 76, 22, 0.1)',
          }
        }}
      >
        <Typography variant="h6" sx={{ 
          fontWeight: 600, 
          color: '#E04C16',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Globe size={20} /> WordPress Connection Guide
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Prerequisites Section */}
          <Box>
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 600, 
              color: '#E04C16',
              mb: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              Prerequisites
            </Typography>
            <Box sx={{ 
              backgroundColor: '#fff8f6', 
              border: '1px solid #fee2d6', 
              borderRadius: 2,
              p: 2
            }}>
              <Typography variant="body2" sx={{ color: '#9a3412', lineHeight: 1.6 }}>
                • Your WordPress site must be accessible via HTTPS<br />
                • You must have Administrator privileges on the WordPress site<br />
                • The WordPress REST API must be enabled<br />
                • Application Passwords feature must be enabled (WordPress 5.6+)
              </Typography>
            </Box>
          </Box>

          {/* Connection Steps */}
          <Box>
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 600, 
              color: '#E04C16',
              mb: 1.5
            }}>
              Connection Steps
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 2,
              pl: 2 
            }}>
              <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                1. Log into your WordPress admin panel<br />
                2. Navigate to Users &gt; Profile<br />
                3. Scroll down to "Application Passwords" section<br />
                4. Enter "Scripti" as the application name<br />
                5. Click "Add New Application Password"<br />
                6. Copy the generated password (looks like: xxxx xxxx xxxx xxxx)<br />
                7. Return to Scripti and click "Add Site"<br />
                8. Enter your site details:<br />
                &nbsp;&nbsp;&nbsp;• Site Name: A memorable name for your site<br />
                &nbsp;&nbsp;&nbsp;• Site URL: Your WordPress site's URL (https://)<br />
                &nbsp;&nbsp;&nbsp;• Username: Your WordPress admin username<br />
                &nbsp;&nbsp;&nbsp;• Password: The application password you just generated
              </Typography>
            </Box>
          </Box>

          {/* Troubleshooting */}
          <Box>
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 600, 
              color: '#E04C16',
              mb: 1.5
            }}>
              Troubleshooting
            </Typography>
            <Box sx={{ 
              backgroundColor: '#f8fafc', 
              border: '1px solid #e2e8f0', 
              borderRadius: 2,
              p: 2
            }}>
              <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                • Ensure your site URL starts with https://<br />
                • Double-check your WordPress username<br />
                • Make sure you're using the application password, not your regular WordPress password<br />
                • Verify that your WordPress user has Administrator role<br />
                • Check if your site has any security plugins that might block API access
              </Typography>
            </Box>
          </Box>

          {/* Security Note */}
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ 
              borderRadius: 2,
              '& .MuiAlert-icon': { color: '#E04C16' },
              '& .MuiAlert-message': { color: '#1f2937' }
            }}>
              For security, we recommend creating a separate WordPress administrator account specifically for Scripti.
            </Alert>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  // Help content for Amazon API
  const amazonHelpContent = (
    <Accordion>
      <AccordionSummary 
        expandIcon={<ExpandMore />}
        sx={{
          backgroundColor: 'rgba(224, 76, 22, 0.05)',
          '&:hover': {
            backgroundColor: 'rgba(224, 76, 22, 0.1)',
          }
        }}
      >
        <Typography variant="h6" sx={{ 
          fontWeight: 600, 
          color: '#E04C16',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <ShoppingBag size={20} /> Amazon API Setup Guide
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Prerequisites Section */}
          <Box>
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 600, 
              color: '#E04C16',
              mb: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              Prerequisites
            </Typography>
            <Box sx={{ 
              backgroundColor: '#fff8f6', 
              border: '1px solid #fee2d6', 
              borderRadius: 2,
              p: 2
            }}>
              <Typography variant="body2" sx={{ color: '#9a3412', lineHeight: 1.6 }}>
                • Active Amazon Associates (Affiliate) Program account<br />
                • Approved status in the Amazon Associates Program<br />
                • Valid tracking ID (Store ID) from your Associates account<br />
                • Access to the Product Advertising API (PA-API)
              </Typography>
            </Box>
          </Box>

          {/* Setup Steps */}
          <Box>
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 600, 
              color: '#E04C16',
              mb: 1.5
            }}>
              Setup Steps
            </Typography>
            <Box sx={{ 
              backgroundColor: '#f8fafc', 
              border: '1px solid #e2e8f0', 
              borderRadius: 2,
              p: 2
            }}>
              <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                1. Sign in to the <a href="https://affiliate-program.amazon.com/" target="_blank" rel="noopener noreferrer" style={{ color: '#E04C16' }}>Amazon Associates Central</a><br />
                2. Navigate to Tools &gt; Product Advertising API<br />
                3. Click on "Manage Your Credentials"<br />
                4. Generate your Access Key ID and Secret Key<br />
                5. Copy both keys immediately (you won't see them again)<br />
                6. Get your Tracking ID from Associates Central &gt; Account Settings<br />
                7. Enter all three values in the fields above
              </Typography>
            </Box>
          </Box>

          {/* API Usage */}
          <Box>
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 600, 
              color: '#E04C16',
              mb: 1.5
            }}>
              API Usage Guidelines
            </Typography>
            <Box sx={{ 
              backgroundColor: '#f8fafc', 
              border: '1px solid #e2e8f0', 
              borderRadius: 2,
              p: 2
            }}>
              <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                • Respect Amazon's API rate limits<br />
                • Only use the API for approved Associates Program purposes<br />
                • Keep your credentials secure and never share them<br />
                • Monitor your API usage in the Amazon Associates dashboard<br />
                • Update your credentials if you notice any unusual activity
              </Typography>
            </Box>
          </Box>

          {/* Security Notice */}
          <Box sx={{ mt: 2 }}>
            <Alert 
              severity="warning" 
              sx={{ 
                borderRadius: 2,
                backgroundColor: '#fff8f6',
                border: '1px solid #fee2d6',
                '& .MuiAlert-icon': { color: '#E04C16' },
                '& .MuiAlert-message': { color: '#9a3412' }
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Important Security Notice
              </Typography>
              <Typography variant="body2">
                Your Amazon API credentials provide direct access to your Associates account. Protect them carefully:
                <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                  <li>Never share your credentials with anyone</li>
                  <li>Rotate your keys regularly (every 90 days recommended)</li>
                  <li>Monitor your API usage for unauthorized access</li>
                  <li>Revoke and replace keys immediately if compromised</li>
                </Box>
              </Typography>
            </Alert>
          </Box>

          {/* Troubleshooting */}
          <Box>
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 600, 
              color: '#E04C16',
              mb: 1.5
            }}>
              Troubleshooting
            </Typography>
            <Box sx={{ 
              backgroundColor: '#f8fafc', 
              border: '1px solid #e2e8f0', 
              borderRadius: 2,
              p: 2
            }}>
              <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                • Verify your Associates account is in good standing<br />
                • Ensure your tracking ID matches your marketplace<br />
                • Check that your API access is active and approved<br />
                • Confirm you're using the correct marketplace endpoint<br />
                • Review Amazon's API documentation for specific error codes
              </Typography>
            </Box>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  useEffect(() => {
    if (!authLoading) {
      fetchWordPressSites();
      fetchApiKeys();
      fetchAmazonSettings();
    }
  }, [authLoading]);

  const fetchWordPressSites = async () => {
    try {
      setIsLoadingWordPress(true);
      setLoadingError(null);
      const token = await getAccessTokenSilently();
      const response = await axios.get(`${config.api.baseUrl}/api/user/wordpress-sites`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setSites(response.data);
    } catch (error) {
      console.error('WordPress sites fetch error:', error);
      setLoadingError(error.response?.data?.detail || 'Error loading WordPress sites');
      setNotification({
        open: true,
        message: 'Error loading WordPress sites: ' + (error.response?.data?.detail || error.message),
        severity: 'error'
      });
    } finally {
      setIsLoadingWordPress(false);
    }
  };

  const fetchApiKeys = async () => {
    try {
      setIsLoadingApiKeys(true);
      const token = await getAccessTokenSilently();
      const response = await axios.get(`${config.api.baseUrl}/api/user/api-keys`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setApiKeys(response.data);
    } catch (error) {
      console.error('API keys fetch error:', error);
      setNotification({
        open: true,
        message: 'Error loading API keys: ' + (error.response?.data?.detail || error.message),
        severity: 'error'
      });
    } finally {
      setIsLoadingApiKeys(false);
    }
  };

  const fetchAmazonSettings = async () => {
    try {
      setIsLoadingAmazon(true);
      const token = await getAccessTokenSilently();
      const response = await axios.get(`${config.api.baseUrl}/api/user/amazon-credentials`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAmazonSettings({
        api_key: response.data.api_key === true ? '••••••••' : response.data.api_key || '',
        secret_key: response.data.secret_key === true ? '••••••••' : response.data.secret_key || '',
        tracking_id: response.data.tracking_id || ''
      });
    } catch (error) {
      console.error('Amazon settings fetch error:', error);
      setNotification({
        open: true,
        message: 'Error loading Amazon settings: ' + (error.response?.data?.detail || error.message),
        severity: 'error'
      });
    } finally {
      setIsLoadingAmazon(false);
    }
  };

  const handleApiKeysSave = async (e) => {
    e.preventDefault();
    try {
      const token = await getAccessTokenSilently();
      await axios.post(`${config.api.baseUrl}/api/user/api-keys`, apiKeys, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setNotification({
        open: true,
        message: 'API keys saved successfully',
        severity: 'success'
      });
    } catch (error) {
      setNotification({
        open: true,
        message: error.response?.data?.detail || 'Failed to save API keys',
        severity: 'error'
      });
    }
  };

  const handleSaveSite = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const errors = {};
    if (!siteForm.name.trim()) errors.name = "Site name is required";
    if (!siteForm.url.trim()) {
      errors.url = "Site URL is required";
    } else {
      let cleanUrl = siteForm.url.trim().toLowerCase();
      cleanUrl = cleanUrl.replace(/\/+$/, '');
      if (!cleanUrl.startsWith('http')) cleanUrl = 'https://' + cleanUrl;
      setSiteForm({ ...siteForm, url: cleanUrl });
    }
    if (!siteForm.username.trim()) errors.username = "Username is required";
    if (!siteForm.password.trim()) errors.password = "Password is required";

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setIsSubmitting(false);
      return;
    }

    try {
      setNotification({
        open: true,
        message: 'Verifying WordPress connection...',
        severity: 'info'
      });
      const token = await getAccessTokenSilently();
      const url = `${config.api.baseUrl}/api/user/wordpress-sites`;
      await axios.post(url, siteForm, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      await fetchWordPressSites();
      handleCloseSiteDialog();
      setNotification({
        open: true,
        message: 'WordPress site connected successfully',
        severity: 'success'
      });
    } catch (error) {
      let errorMessage = 'Failed to connect to WordPress site';
      if (error.response?.data?.detail) errorMessage = error.response.data.detail;
      else if (error.response?.status === 404) errorMessage = "WordPress REST API not found.";
      else if (error.response?.status === 401) errorMessage = "Invalid credentials.";
      else if (error.response?.status === 403) errorMessage = "Access denied.";
      else if (error.message.includes('Network Error')) errorMessage = "Could not connect.";
      
      setNotification({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDeleteDialog = (site) => {
    setSiteToDelete(site);
    setDeleteDialogOpen(true);
  };

  const handleDeleteSite = async () => {
    try {
      setDeleteLoading(true);
      const token = await getAccessTokenSilently();
      await axios.delete(`${config.api.baseUrl}/api/user/wordpress-sites/${siteToDelete.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDeleteDialogOpen(false);
      await fetchWordPressSites();
      setNotification({
        open: true,
        message: 'Site deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      setNotification({
        open: true,
        message: 'Error deleting WordPress site',
        severity: 'error'
      });
    } finally {
      setDeleteLoading(false);
      setSiteToDelete(null);
    }
  };

  const handleOpenSiteDialog = () => {
    setSiteForm({ name: '', url: '', username: '', password: '' });
    setSelectedSite(null);
    setFormErrors({});
    setIsSubmitting(false);
    setOpenDialog(true);
  };

  const handleCloseSiteDialog = () => {
    setOpenDialog(false);
    setSelectedSite(null);
    setFormErrors({});
    setIsSubmitting(false);
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleSaveAmazonSettings = async (settings) => {
    try {
      if (Object.keys(settings).length === 0) return;
      const token = await getAccessTokenSilently();
      await axios.post(`${config.api.baseUrl}/api/user/amazon-credentials`, settings, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      await fetchAmazonSettings();
      setNotification({
        open: true,
        message: 'Amazon settings saved successfully',
        severity: 'success'
      });
    } catch (error) {
      setNotification({
        open: true,
        message: error.response?.data?.detail || 'Failed to save Amazon settings',
        severity: 'error'
      });
    }
  };

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  const tabIcons = {
    0: <UserIcon size={20} />,
    1: <Key size={20} />,
    2: <Globe size={20} />,
    3: <ShoppingBag size={20} />
  };

  return (
    <Box>
      <PageHeader
        title="Account Settings"
        icon={<Settings size={24} />}
        description="Manage your account settings, API keys, and connected services."
      />

      <Box sx={{ 
        maxWidth: '1200px', 
        mx: 'auto', 
        px: 4,
        pb: 6
      }}>
        <Paper elevation={0} sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          background: '#fff',
          border: '1px solid rgba(0,0,0,0.1)'
        }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ 
              borderBottom: '1px solid rgba(0,0,0,0.1)',
              backgroundColor: '#f8fafc',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.95rem',
                minHeight: '56px',
                gap: 1
              },
              '& .Mui-selected': {
                color: '#E04C16',
                backgroundColor: '#fff'
              }
            }}
            TabIndicatorProps={{ 
              style: { 
                backgroundColor: '#E04C16',
                height: 3
              } 
            }}
          >
            {['Profile', 'API Keys', 'WordPress Sites', 'Amazon API'].map((label, index) => (
              <Tab 
                key={label}
                label={label}
                icon={tabIcons[index]}
                iconPosition="start"
              />
            ))}
          </Tabs>

          {/* Profile Tab */}
          <TabPanel value={activeTab} index={0}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Card elevation={0} sx={{ 
                  backgroundColor: '#f8fafc',
                  borderRadius: 3,
                  border: '1px solid rgba(0,0,0,0.1)'
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="overline" color="text.secondary">
                        Account Details
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      {user?.picture ? (
                        <img 
                          src={user.picture} 
                          alt={user.name}
                          style={{ 
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <Box sx={{ 
                          width: 64,
                          height: 64,
                          borderRadius: '50%',
                          backgroundColor: '#E04C16',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontSize: '1.5rem',
                          fontWeight: 600
                        }}>
                          {user?.name?.charAt(0)}
                        </Box>
                      )}
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {user?.name}
                        </Typography>
                        <Typography color="text.secondary">
                          {user?.email}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip 
                      label="Email Verified"
                      color={user?.email_verified ? "success" : "warning"}
                      size="small"
                      sx={{ borderRadius: 2 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* API Keys Tab */}
          <TabPanel value={activeTab} index={1}>
            {isLoadingApiKeys ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                <form onSubmit={handleApiKeysSave}>
                  <Grid container spacing={3}>
                    {[
                      { label: "OpenAI API Key", key: "openai", helper: "Required for content generation" },
                      { label: "Anthropic API Key", key: "anthropic", helper: "Optional: For Claude model access" },
                      { label: "OpenRouter API Key", key: "openrouter", helper: "Optional: For additional AI models" },
                      { label: "Fal.ai API Key", key: "fal", helper: "Required for image generation" },
                      { label: "ScrapeOwl API Key", key: "scrapeowl", helper: "Required for URL Rewriter" }
                    ].map((field) => (
                      <Grid item xs={12} key={field.key}>
                        <TextField
                          fullWidth
                          label={field.label}
                          type="password"
                          value={apiKeys[field.key]}
                          onChange={(e) => setApiKeys({ ...apiKeys, [field.key]: e.target.value })}
                          variant="outlined"
                          helperText={field.helper}
                          sx={{ 
                            '& .MuiOutlinedInput-root': { 
                              borderRadius: 2,
                              backgroundColor: '#f8fafc',
                              '&:hover': {
                                backgroundColor: '#f1f5f9'
                              }
                            }
                          }}
                        />
                      </Grid>
                    ))}
                    <Grid item xs={12}>
                      <Button 
                        type="submit" 
                        variant="contained" 
                        size="large"
                        sx={{ 
                          borderRadius: 2,
                          px: 4,
                          py: 1.5,
                          backgroundColor: '#E04C16',
                          '&:hover': { 
                            backgroundColor: '#c2410c' 
                          }
                        }}
                      >
                        Save API Keys
                      </Button>
                    </Grid>
                  </Grid>
                </form>
                <Box sx={{ mt: 6 }}>{apiHelpContent}</Box>
              </Box>
            )}
          </TabPanel>

          {/* WordPress Sites Tab */}
          <TabPanel value={activeTab} index={2}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 4 
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Connected WordPress Sites
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Plus size={18} />}
                  onClick={handleOpenSiteDialog}
                  sx={{ 
                    borderRadius: 2,
                    px: 3,
                    backgroundColor: '#E04C16',
                    '&:hover': { 
                      backgroundColor: '#c2410c' 
                    }
                  }}
                >
                  Add Site
                </Button>
              </Box>

              {isLoadingWordPress ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : loadingError ? (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  {loadingError}
                </Alert>
              ) : sites.length === 0 ? (
                <Alert 
                  severity="info" 
                  sx={{ 
                    mb: 2,
                    borderRadius: 2,
                    backgroundColor: '#f0f9ff',
                    border: '1px solid #bae6fd'
                  }}
                >
                  No WordPress sites added yet. Click "Add Site" to get started.
                </Alert>
              ) : (
                <Grid container spacing={3}>
                  {sites.map((site) => (
                    <Grid item xs={12} md={6} key={site.id}>
                      <Card sx={{ 
                        borderRadius: 3,
                        border: '1px solid rgba(0,0,0,0.1)',
                        transition: 'all 0.2s',
                        '&:hover': { 
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 24px rgba(0,0,0,0.1)'
                        }
                      }}>
                        <CardContent sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 2,
                          p: 3
                        }}>
                          <Box sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            backgroundColor: '#f8fafc',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid rgba(0,0,0,0.1)'
                          }}>
                            <img 
                              src={`${site.url}/favicon.ico`}
                              alt=""
                              onError={(e) => { e.target.src = `https://www.google.com/s2/favicons?domain=${site.url}`; }}
                              style={{ width: 20, height: 20, objectFit: 'contain' }}
                            />
                          </Box>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" sx={{ 
                              fontWeight: 600,
                              fontSize: '1.1rem',
                              mb: 0.5
                            }}>
                              {site.name}
                            </Typography>
                            <Typography 
                              color="text.secondary" 
                              variant="body2"
                              sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}
                            >
                              <Globe size={14} />
                              {site.url}
                            </Typography>
                          </Box>
                          <IconButton 
                            onClick={() => handleOpenDeleteDialog(site)}
                            sx={{ 
                              color: '#ef4444',
                              '&:hover': {
                                backgroundColor: '#fef2f2'
                              }
                            }}
                          >
                            <Trash size={18} />
                          </IconButton>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
              <Box sx={{ mt: 6 }}>{wordpressHelpContent}</Box>
            </Box>
          </TabPanel>

          {/* Amazon API Tab */}
          <TabPanel value={activeTab} index={3}>
            {isLoadingAmazon ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                <AmazonSettings
                  onSave={handleSaveAmazonSettings}
                  initialData={amazonSettings}
                  isLoading={false}
                />
                <Box sx={{ mt: 6 }}>{amazonHelpContent}</Box>
              </Box>
            )}
          </TabPanel>
        </Paper>
      </Box>

      {/* WordPress Site Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseSiteDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <form onSubmit={handleSaveSite}>
          <DialogTitle sx={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #eee' }}>
            Add WordPress Site
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              {[
                { label: "Site Name", key: "name" },
                { label: "Site URL", key: "url", helper: "Example: your-site.com" },
                { label: "Username", key: "username" },
                { label: "Password", key: "password", type: "password" }
              ].map((field) => (
                <Grid item xs={12} key={field.key}>
                  <TextField
                    fullWidth
                    label={field.label}
                    type={field.type || "text"}
                    value={siteForm[field.key]}
                    onChange={(e) => setSiteForm({ ...siteForm, [field.key]: e.target.value })}
                    required
                    helperText={formErrors[field.key] || field.helper}
                    variant="outlined"
                    error={!!formErrors[field.key]}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#fff' } }}
                  />
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseSiteDialog} sx={{ borderRadius: 2 }}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              disabled={isSubmitting}
              sx={{ borderRadius: 2, px: 3, backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' } }}
            >
              {isSubmitting ? <CircularProgress size={24} /> : 'Add Site'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Trash color="#ef4444" size={20} />
            <Typography variant="h6" component="span">Delete WordPress Site</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
            This action cannot be undone. The site will be disconnected from Scripti.sh.
          </Alert>
          <Typography>
            Are you sure you want to delete the connection to <strong>{siteToDelete?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteSite}
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : <Trash size={20} />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete Site'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%', borderRadius: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default UserDashboard;